import { Button, DatePicker, Space, TimePicker, Tooltip } from "antd";
import TextArea from "antd/es/input/TextArea";
import { TableProps } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import moment from "moment-timezone";
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import BackButtonIcon from "../../../assets/icons/BackButtonIcon";
import CloseIcon from "../../../assets/icons/CloseIcon";
import DateIcon from "../../../assets/icons/DateIcon";
import DoubleCheckIcon from "../../../assets/icons/DoubleCheckIcon";
import DownloadIcon from "../../../assets/icons/DownloadIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import ReloadIcon from "../../../assets/icons/ReloadIcon";
import RemarkIcon from "../../../assets/icons/RemarkIcon";
import SettingIcon from "../../../assets/icons/SettingIcon";
import SortIcon from "../../../assets/icons/SortIcon";
import SummaryIcon from "../../../assets/icons/SummaryIcon";
import UserIcon from "../../../assets/icons/UserIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import UserImg from "../../../assets/images/avatar.svg";
import AttendanceLogModal from "../../../components/Attendance/AttendanceLogModal";
import InfoTitle from "../../../components/common/InfoTitle";
import TopCard from "../../../components/common/TopCard";
import ReportRequestModal from "../../../components/Report/ReportRequestModal";
import CustomTable from "../../../components/ui/CustomTable";
import colors from "../../../constants/colors";
import {
  dateFormat,
  dbDateFormat,
  dbTimeFormat,
  timeFormat,
} from "../../../constants/constants";
import {
  useAttendanceReconciliationMutation,
  useGetCustomAttendanceByEmployeeQuery,
} from "../../../features/attendanceReport/attendanceReportApi";
import { useGetHolidaysQuery } from "../../../features/holiday/holidayApi";
import { useCustomAttendanceReportByEmployeeMutation } from "../../../features/report/reportApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import useWindowWidth from "../../../hooks/useWindowWidth";
import SidebarLayout from "../../../layouts/SidebarLayout";
import {
  convertMinutesToHoursWithText,
  convertMinutesToTime,
} from "../../../lib/time";
import { toast } from "../../../lib/toast";
import { GetCustomAttendanceByEmployeeResponseType } from "../../../types/attendance";
import {
  addKeyInArray,
  backLink,
  underscoreToCapitalizeString,
} from "../../../utils/utils";
import NotFound from "../../NotFound";

dayjs.extend(customParseFormat);

interface EditAttendanceType {
  id: number;
  checkin_time?: string | null;
  checkout_time?: string | null;
  index: number;
  remarks?: string;
  date?: string;
}

type TableColType =
  GetCustomAttendanceByEmployeeResponseType["data"]["attendance_logs"][0];

const attendanceColors = {
  "Working Days": "#4a71ff",
  "Working Hours": "#de5df1",
  Present: colors.complete,
  Absent: colors.header,
  "On leave": "#afbaca",
  "On time": colors.complete,
  Late: "#ff7a71",
  "Left Timely": colors.complete,
  "Left Early": "#ff7a71",
} as const;

const statusColors = {
  on_time: {
    color: colors.complete,
    text: "On time",
  },
  late: {
    color: colors.danger?.color,
    text: "Late",
  },
  absent: {
    color: colors.header,
    text: "Absent",
  },
  left_early: {
    color: colors.danger?.color,
    text: "Left Early",
  },
  left_timely: {
    color: colors.success?.color,
    text: "Left Timely",
  },
} as const;

const leaveStatusList = ["on_leave", "on leave", "leave"] as const;

const normalizeToDbDate = (value?: string | null) => {
  if (!value) return "";
  const dateMatch = value.match(/\d{4}-\d{2}-\d{2}/);
  if (dateMatch?.[0]) return dateMatch[0];
  return value.slice(0, 10);
};

const getAttendanceStatusMeta = (
  record: TableColType,
  holidayNameByDate?: string,
  holidayNameById?: string
) => {
  const normalizedStatus = (record?.status || "").toLowerCase();

  const leaveTypeName =
    record?.leave_type_title ||
    record?.leave_type ||
    record?.leave_name ||
    record?.leave?.title ||
    record?.leave?.name ||
    record?.leave?.leave_type?.title ||
    record?.leave?.leave_type?.name;
  const isHalfDayLeave = Number(record?.is_half_day || 0) === 1;

  const calendarHolidayName = holidayNameById || holidayNameByDate;
  const holidayName =
    calendarHolidayName ||
    record?.holiday_title ||
    record?.holiday_name ||
    record?.holiday?.title ||
    record?.holiday?.name;

  const isLeave =
    leaveStatusList.includes(
      normalizedStatus as (typeof leaveStatusList)[number]
    ) ||
    !!record?.is_leave ||
    !!record?.is_leave_taken;

  if (isLeave) {
    return {
      color: "#c96f69",
      text: leaveTypeName
        ? `${leaveTypeName}${isHalfDayLeave ? " (Half Day)" : ""}`
        : isHalfDayLeave
          ? "Half Day Leave"
          : "On Leave",
    };
  }

  const isHoliday =
    !!record?.is_holiday ||
    normalizedStatus === "holiday" ||
    (!!calendarHolidayName && !isLeave && !record?.is_weekend);

  if (isHoliday) {
    return {
      color: colors.extraGray,
      text: holidayName || "Holiday",
    };
  }

  if (record?.is_weekend) {
    return {
      color: colors.extraGray,
      text: "Weekend",
    };
  }

  const baseStatus = statusColors[normalizedStatus as keyof typeof statusColors];

  return {
    color: baseStatus?.color || colors.extraGray,
    text: baseStatus?.text || record?.status || "n/a",
  };
};

type ModalType = "LOG" | "REPORT_REQUEST";

export default function CustomAttendanceDetails() {
  const location = useLocation();
  const { viewId } = useParams();
  const [editAttendance, setEditAttendance] = useState<
    EditAttendanceType | undefined
  >(undefined);
  const [dates, setDates] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("month"),
    dayjs().endOf("month"),
  ]);
  const { open, handleClose, handleOpen } = useModal<number, ModalType>();
  const { isLoading, isError, error, data } =
    useGetCustomAttendanceByEmployeeQuery(
      {
        params: {
          business_member_id: Number(viewId || 0),
          from_date: dates[0].format(dbDateFormat),
          to_date: dates[1].format(dbDateFormat),
        },
      },
      {
        skip: !viewId,
      }
    );
  const startYear = dates[0].year();
  const endYear = dates[1].year();
  const {
    isError: isHolidayListError,
    error: holidayListError,
    data: holidayListData,
  } = useGetHolidaysQuery(
    {
      params: {
        year: startYear,
        skip: 0,
        limit: 0,
      },
    },
    {
      skip: !dates?.length,
    }
  );
  const {
    isError: isHolidayListEndYearError,
    error: holidayListEndYearError,
    data: holidayListEndYearData,
  } = useGetHolidaysQuery(
    {
      params: {
        year: endYear,
        skip: 0,
        limit: 0,
      },
    },
    {
      skip: !dates?.length || startYear === endYear,
    }
  );
  const [
    attendanceReconciliation,
    {
      isLoading: reconciliationLoading,
      isError: isReconciliationError,
      error: reconciliationError,
      isSuccess: isReconciliationSuccess,
    },
  ] = useAttendanceReconciliationMutation();
  const width = useWindowWidth();
  const [
    customAttendanceReportByEmployee,
    {
      isLoading: isGenerateLoading,
      isError: isGenerateError,
      error: generateError,
      isSuccess: isGenerateSuccess,
    },
  ] = useCustomAttendanceReportByEmployeeMutation();
  useError(isGenerateError, generateError, "Generate request report fail");
  useError(isError, error, "Get employee data fail");
  useError(isHolidayListError, holidayListError, "Get holiday data fail");
  useError(
    isHolidayListEndYearError,
    holidayListEndYearError,
    "Get holiday data fail"
  );
  useError(
    isReconciliationError,
    reconciliationError,
    "Attendance times updated fail"
  );

  const attendanceSummary = data?.data?.stats;
  const userDetails = data?.data?.user_details;
  const holidayTitleByDate = useMemo(() => {
    const map = new Map<string, string>();
    const holidays = [
      ...(holidayListData?.data?.holidays || []),
      ...(holidayListEndYearData?.data?.holidays || []),
    ];
    const rangeStart = dates[0].startOf("day");
    const rangeEnd = dates[1].endOf("day");

    holidays.forEach((holiday) => {
      const startDateText = normalizeToDbDate(holiday?.start_date);
      const endDateText = normalizeToDbDate(
        holiday?.end_date || holiday?.start_date
      );
      const startDate = dayjs(startDateText, dbDateFormat).startOf("day");
      const endDate = dayjs(endDateText, dbDateFormat).endOf("day");

      if (!startDate.isValid() || !endDate.isValid()) return;
      if (endDate.isBefore(rangeStart) || startDate.isAfter(rangeEnd)) return;

      let current = startDate;
      while (current.isSame(endDate, "day") || current.isBefore(endDate, "day")) {
        map.set(current.format(dbDateFormat), holiday?.title);
        current = current.add(1, "day");
      }
    });

    return map;
  }, [
    holidayListData?.data?.holidays,
    holidayListEndYearData?.data?.holidays,
    dates,
  ]);
  const holidayTitleById = useMemo(() => {
    const map = new Map<number, string>();
    const holidays = [
      ...(holidayListData?.data?.holidays || []),
      ...(holidayListEndYearData?.data?.holidays || []),
    ];

    holidays.forEach((holiday) => {
      if (holiday?.id) {
        map.set(holiday.id, holiday.title);
      }
    });

    return map;
  }, [holidayListData?.data?.holidays, holidayListEndYearData?.data?.holidays]);

  const handleSubmit = () => {
    if (!editAttendance?.checkin_time && !editAttendance?.checkout_time) {
      toast("error", "Please provide at least check-in or check-out time");
      return;
    }

    if (!editAttendance?.remarks) {
      toast("error", "Please fill the remarks field");
      return;
    }

    attendanceReconciliation({
      date: editAttendance?.date,
      business_member_id: Number(viewId),
      start_time: editAttendance?.checkin_time,
      end_time: editAttendance?.checkout_time,
      remarks: editAttendance?.remarks,
    });
  };

  const columns: TableProps<TableColType>["columns"] = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Date",
      dataIndex: "date",
      sorter: (a, b) =>
        dayjs(a.date, dbDateFormat).valueOf() -
        dayjs(b.date, dbDateFormat).valueOf(),
      sortIcon: () => <SortIcon />,
      render: (val) => (
        <span className="min-w-[100px] inline-block">
          {dayjs(val, dbDateFormat).format(dateFormat)}
        </span>
      ),
    },
    {
      title: <div className="w-[75px]">Shift Name</div>,
      dataIndex: "shift_name",
      // render: (val) => (val ? underscoreToCapitalizeString(val) : "n/a"),
      sorter: (a, b) => a?.shift_name?.localeCompare(b?.shift_name),
      sortIcon: () => <SortIcon />,
      render: (val) => (val ? underscoreToCapitalizeString(val) : "n/a"),
    },
    {
      title: (
        <div className={editAttendance ? "w-[100px]" : "w-[60px]"}>
          Check In
        </div>
      ),
      // dataIndex: "checkin_time",

      sorter: (a, b) =>
        dayjs(a.checkin_time, dbTimeFormat).valueOf() -
        dayjs(b.checkin_time, dbTimeFormat).valueOf(),
      sortIcon: () => <SortIcon />,
      render: (val: TableColType, _, idx) =>
        editAttendance?.index === idx ? (
          <TimePicker
            use12Hours
            format={timeFormat}
            value={
              val?.checkin_time === "Absent" ||
              !val?.checkin_time ||
              !editAttendance?.checkin_time
                ? undefined
                : dayjs(editAttendance?.checkin_time, dbTimeFormat)
            }
            onChange={(e) =>
              setEditAttendance(
                (prev) =>
                  prev && {
                    ...prev,
                    checkin_time: e?.format(dbTimeFormat),
                  }
              )
            }
          />
        ) : val?.checkin_time ? (
          val?.is_attendance_reconciled ? (
            <Tooltip
              placement="top"
              title={`Reconciliation by super admin (${val?.checkin_log?.updated_by_name})`}
            >
              <span
                className="min-w-[110px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white cursor-pointer"
                style={{
                  backgroundColor:
                    val?.checkin_log?.status === "on_time" ||
                    val?.checkin_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                <ReloadIcon />
                {dayjs(val?.checkin_time, dbTimeFormat).format("h:mm:ss A")}
              </span>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-2">
              <span
                className="min-w-[110px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white "
                style={{
                  backgroundColor:
                    val?.checkin_log?.status === "on_time" ||
                    val?.checkin_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                {dayjs(val?.checkin_time, dbTimeFormat).format("h:mm:ss A")}
              </span>

              {!!val?.checkin_log?.is_auto_wifi_attendance ? (
                <span className="tooltip-box">A</span>
              ) : val?.checkin_log?.is_in_wifi ? (
                <Tooltip
                  placement="top"
                  title={val?.checkin_log?.business_office?.name}
                >
                  <span className="tooltip-box">W</span>
                </Tooltip>
              ) : val?.checkin_log?.is_remote ? (
                <Tooltip
                  placement="top"
                  title={val?.checkin_log?.location?.address}
                >
                  <span className="tooltip-box">H</span>
                </Tooltip>
              ) : val?.checkin_log?.is_geo_location ? (
                <Tooltip
                  placement="top"
                  title={val?.checkin_log?.business_office?.name}
                >
                  <span className="tooltip-box">G</span>
                </Tooltip>
              ) : null}
              {val?.checkin_log?.note ? (
                <Tooltip placement="top" title={val?.checkin_log?.note}>
                  <div>
                    <RemarkIcon className="cursor-pointer" />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          )
        ) : (
          "n/a"
        ),
    },
    {
      title: (
        <div className={editAttendance ? "w-[100px]" : "w-[75px]"}>
          Check Out
        </div>
      ),
      //   dataIndex: "checkout_time",
      sorter: (a, b) =>
        dayjs(a.checkout_time, dbTimeFormat).valueOf() -
        dayjs(b.checkout_time, dbTimeFormat).valueOf(),
      sortIcon: () => <SortIcon />,
      render: (val: TableColType, _, idx) =>
        editAttendance?.index === idx ? (
          <TimePicker
            use12Hours
            format={timeFormat}
            value={
              val?.checkout_time === "Absent" ||
              !val?.checkout_time ||
              !editAttendance?.checkout_time
                ? undefined
                : dayjs(editAttendance?.checkout_time, dbTimeFormat)
            }
            onChange={(e) =>
              setEditAttendance(
                (prev) =>
                  prev && {
                    ...prev,
                    checkout_time: e?.format(dbTimeFormat),
                  }
              )
            }
          />
        ) : val?.checkout_time ? (
          val?.is_attendance_reconciled ? (
            <Tooltip
              placement="top"
              title={`Reconciliation by super admin (${val?.checkout_log?.updated_by_name})`}
            >
              <span
                className="min-w-[100px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white cursor-pointer"
                style={{
                  backgroundColor:
                    val?.checkout_log?.status === "on_time" ||
                    val?.checkout_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                <ReloadIcon />
                {dayjs(val?.checkout_time, dbTimeFormat).format("h:mm:ss A")}
              </span>
            </Tooltip>
          ) : (
            <div className="flex items-center gap-2">
              <span
                className="min-w-[100px] px-2 py-0.5 rounded-md justify-center items-center gap-1.5 inline-flex text-white"
                style={{
                  backgroundColor:
                    val?.checkout_log?.status === "on_time" ||
                    val?.checkout_log?.status === "left_timely"
                      ? colors.success?.color
                      : colors?.danger?.color,
                }}
              >
                {dayjs(val?.checkout_time, dbTimeFormat).format("h:mm:ss A")}
              </span>

              {!!val?.checkout_log?.is_auto_wifi_attendance ? (
                <span className="tooltip-box">A</span>
              ) : val?.checkout_log?.is_in_wifi ? (
                <Tooltip
                  placement="top"
                  title={val?.checkout_log?.business_office?.name}
                >
                  <span className="tooltip-box">W</span>
                </Tooltip>
              ) : val?.checkout_log?.is_remote ? (
                <Tooltip
                  placement="top"
                  title={val?.checkout_log?.location?.address}
                >
                  <span className="tooltip-box">H</span>
                </Tooltip>
              ) : val?.checkout_log?.is_geo_location ? (
                <Tooltip
                  placement="top"
                  title={val?.checkout_log?.business_office?.name}
                >
                  <span className="tooltip-box">G</span>
                </Tooltip>
              ) : null}
              {val?.checkout_log?.note ? (
                <Tooltip placement="top" title={val?.checkout_log?.note}>
                  <div>
                    <RemarkIcon className="cursor-pointer" />
                  </div>
                </Tooltip>
              ) : null}
            </div>
          )
        ) : (
          "n/a"
        ),
    },
    {
      title: "Log",
      dataIndex: "attendance_id",
      render: (val: number) => {
        return val ? (
          <Button
            className="download-button !p-0 !h-fit !border-none !shadow-none"
            size="small"
            icon={<ViewIcon />}
            onClick={() => handleOpen({ type: "LOG", data: val })}
          >
            View
          </Button>
        ) : (
          "-"
        );
      },
    },
    {
      title: () => (
        <span className="min-w-[120px] inline-block">Net Office Hours</span>
      ),
      dataIndex: "staying_time_in_minutes",
      render: (val: number) => convertMinutesToHoursWithText(val || 0),
      sorter: (a, b) => a.staying_time_in_minutes - b.staying_time_in_minutes,
      sortIcon: () => <SortIcon />,
    },
    {
      title: () => (
        <span className="min-w-[140px] inline-block">Total Working Hours</span>
      ),
      render: (val: any) => {
        const calculateWorkingMinutes = (el: any): number => {
          if (!el?.checkin_time || !el?.checkout_time || !el?.date) {
            return 0;
          }

          const baseDate = moment(el.date);

          if (!baseDate.isValid()) {
            return 0;
          }

          const checkInTime = moment(el.checkin_time, "HH:mm:ss");
          const checkOutTime = moment(el.checkout_time, "HH:mm:ss");

          if (!checkInTime.isValid() || !checkOutTime.isValid()) {
            return 0;
          }

          const checkIn = moment.tz(
            `${baseDate.format("YYYY-MM-DD")} ${el.checkin_time}`,
            "YYYY-MM-DD HH:mm:ss",
            "Asia/Dhaka"
          );

          let checkOutDate = baseDate;
          if (checkInTime.isAfter(checkOutTime, "second")) {
            checkOutDate = checkOutDate.add(1, "day");
          }

          const checkOut = moment.tz(
            `${checkOutDate.format("YYYY-MM-DD")} ${el.checkout_time}`,
            "YYYY-MM-DD HH:mm:ss",
            "Asia/Dhaka"
          );

          if (!checkIn.isValid() || !checkOut.isValid()) {
            return 0;
          }

          const diffMinutes = checkOut.diff(checkIn, "minutes");

          return diffMinutes > 0 ? diffMinutes : 0;
        };

        return (
          <span className="text-nowrap">
            {convertMinutesToHoursWithText(calculateWorkingMinutes(val))}
          </span>
        );
      },
    },
    {
      title: "Overtime",
      dataIndex: "overtime_in_minutes",
      render: (val) => convertMinutesToHoursWithText(val || 0),
      sorter: (a, b) =>
        dayjs(a.overtime_in_minutes, "hh:mm:ss").valueOf() -
        dayjs(b.overtime_in_minutes, "hh:mm:ss").valueOf(),
      sortIcon: () => <SortIcon />,
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      width: 200,
      render: (val, _, idx) =>
        editAttendance?.index === idx ? (
          <TextArea
            value={editAttendance?.remarks}
            onChange={(e) =>
              setEditAttendance(
                (prev) =>
                  prev && {
                    ...prev,
                    remarks: e.target.value,
                  }
              )
            }
          />
        ) : (
          <Tooltip title={val || "-"}>
            <div className="w-[200px] text-ellipsis overflow-hidden whitespace-nowrap">
              {val || "-"}
            </div>
          </Tooltip>
        ),
    },
    {
      title: "Status",
      align: "center",
      render: (_, record) => {
        const status = getAttendanceStatusMeta(
          record,
          holidayTitleByDate.get(normalizeToDbDate(record?.date)),
          holidayTitleById.get(Number(record?.is_holiday || 0))
        );

        return (
          <Tooltip title={status.text}>
            <span
              className="min-h-7 px-2 py-0.5 rounded-md justify-center items-center inline-flex text-center text-white text-sm font-medium max-w-[180px] whitespace-normal break-words leading-5"
              style={{
                backgroundColor: status.color,
              }}
            >
              {status.text}
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Action",
      render: (val: TableColType, _, idx) => {
        const buttonClass =
          "w-7 h-7 bg-white rounded-full border justify-center items-center inline-flex cursor-pointer select-none";

        return (
          <Space align="center" size={10}>
            {editAttendance?.index === idx ? (
              <>
                <Button
                  className={`${buttonClass} border-[#8fe6b7] text-complete !w-7 !h-7 middle-icon`}
                  icon={<DoubleCheckIcon />}
                  loading={reconciliationLoading}
                  onClick={handleSubmit}
                />

                <span
                  className={`${buttonClass} border-[#ffc5c1] text-dark-highlight`}
                  onClick={() => setEditAttendance(undefined)}
                >
                  <CloseIcon />
                </span>
              </>
            ) : (
              <span
                className={`${buttonClass} border-[#b4c4ff] text-accent`}
                onClick={() =>
                  setEditAttendance({
                    id: val?.id,
                    checkin_time: val?.checkin_time
                      ? val?.checkin_time
                      : undefined,
                    checkout_time: val?.checkout_time
                      ? val?.checkout_time
                      : undefined,
                    index: idx,
                    remarks: val?.remarks || undefined,
                    date: val?.date,
                  })
                }
              >
                <EditIcon />
              </span>
            )}
          </Space>
        );
      },
    },
  ];

  // success toast message
  useEffect(() => {
    if (isReconciliationSuccess) {
      toast("success", "Attendance times updated successfully");
      setEditAttendance(undefined);
    }
  }, [isReconciliationSuccess]);

  if (error && "status" in error && error.status === 404) {
    return <NotFound title="Sorry, data not found" />;
  }

  return (
    <SidebarLayout title="Custom Attendance Details">
      {open?.type === "LOG" && (
        <AttendanceLogModal
          {...{
            ...open,
            type: open.type as ModalType,
          }}
          handleClose={handleClose}
        />
      )}
      {open.type === "REPORT_REQUEST" && (
        <ReportRequestModal
          open={open.open}
          handleClose={handleClose}
          isLoading={isGenerateLoading}
          isSuccess={isGenerateSuccess}
          columnList={columnList}
          handleSubmit={(dates: any, selected) => {
            customAttendanceReportByEmployee({
              body: {
                name: "Attendance Report by Employee",
                column_list: selected,
              },
              params: {
                business_member_id: viewId,
                from_date: dates?.start_date,
                to_date: dates?.end_date,
              },
            });
          }}
        />
      )}
      <TopCard
        leftChildren={
          <Link to={backLink(location.pathname, 2)}>
            <Button className="normal-dark-button" icon={<BackButtonIcon />}>
              Back
            </Button>
          </Link>
        }
      >
        {/* <Button
          className="download-button"
          icon={<DownloadIcon />}
          loading={excelLoading}
          onClick={handleDownload}
        >
          Export Report
        </Button> */}
        <Button
          onClick={() => handleOpen({ type: "REPORT_REQUEST" })}
          className="download-button"
          icon={<DownloadIcon />}
        >
          Report Requests
        </Button>
        <Link to="/admin/settings/office-settings/attendance">
          <Button
            type="primary"
            className="middle-button"
            icon={<SettingIcon />}
          />
        </Link>
      </TopCard>

      <div className="grid xl:grid-cols-5 gap-6 mb-6">
        <div className="xl:col-span-2 w-full">
          <div className="bg-white rounded-md border border-extra-light p-6 w-full">
            <InfoTitle className="mb-6" icon={<UserIcon />}>
              Employee Info
            </InfoTitle>

            <Space align="center" className="w-full" size={16}>
              <div className="w-24 h-24">
                <img
                  className="w-full h-full rounded-full"
                  src={userDetails?.image || UserImg}
                  alt=""
                />
              </div>
              <div className="flex flex-col gap-0.5 w-full">
                <h3 className="text-body text-lg font-semibold leading-normal">
                  {userDetails?.name}
                </h3>
                <p className="text-extra-gray text-sm font-medium leading-normal">
                  ID: {userDetails?.employee_id ?? "n/a"}
                </p>
                <p className="text-display-body text-sm font-medium leading-normal">
                  {userDetails?.designation || `Designation: n/a`}
                </p>
                <p className="text-display-body text-sm font-medium leading-normal">
                  Department of
                  <span className="text-tint-warning ml-1.5">
                    {userDetails?.department || "n/a"}
                  </span>
                </p>
                {/* <p className="text-display-body text-sm font-medium leading-normal">
                  Email:
                  <a
                    href={`mailto:${userDetails?.email}`}
                    className="!text-accent hover:underline ml-1.5"
                  >
                    {userDetails?.email}
                  </a>
                </p> */}

                {/* Email in a single line with ellipsis */}
                <p className="text-display-body text-sm font-medium leading-normal flex items-center">
                  Email:{" "}
                  <Tooltip title={userDetails?.email || "N/A"}>
                    <a
                      className="text-accent ml-1.5 hover:underline truncate block !whitespace-normal"
                      href={`mailto:${userDetails?.email}`}
                      style={{
                        maxWidth:
                          width > 1480
                            ? width / 6
                            : width > 1300
                            ? width / 6.6
                            : width > 1279
                            ? width / 7.5
                            : width < 550
                            ? width / 2
                            : "100%",
                      }}
                    >
                      {userDetails?.email || "N/A"}
                    </a>
                  </Tooltip>
                </p>
              </div>
            </Space>
          </div>
        </div>

        <div className="xl:col-span-3 bg-white rounded-md border border-extra-light small-input-section p-6">
          <Space
            className="w-full mb-6 justify-between"
            align="center"
            size={8}
          >
            <InfoTitle icon={<SummaryIcon />}>Summary</InfoTitle>

            <DatePicker.RangePicker
              format={dateFormat}
              style={{ width: 260 }}
              suffixIcon={<DateIcon />}
              value={dates}
              onChange={(dates) => dates && setDates(dates as [Dayjs, Dayjs])}
              allowClear={false}
              className="clear-button-hide"
            />
          </Space>

          <div className="grid md:grid-cols-2 gap-x-6">
            <SummaryItem
              title="Working Days:"
              color={attendanceColors["Working Days"]}
              count={attendanceSummary?.working_days}
            />
            <SummaryItem
              title="On time:"
              color={attendanceColors["On time"]}
              count={attendanceSummary?.on_time}
            />
            <SummaryItem
              title="Working Hours:"
              color={attendanceColors["Working Hours"]}
              count={
                convertMinutesToTime(attendanceSummary?.total_minutes) + " H"
              }
              hideDays
            />
            <SummaryItem
              title="Late:"
              color={attendanceColors["Late"]}
              count={attendanceSummary?.late}
            />
            <SummaryItem
              title="Present:"
              color={attendanceColors["Present"]}
              count={attendanceSummary?.present}
            />
            <SummaryItem
              title="Left Timely:"
              color={attendanceColors["Left Timely"]}
              count={attendanceSummary?.left_timely}
            />
            <SummaryItem
              title="Absent:"
              color={attendanceColors["Absent"]}
              count={attendanceSummary?.absent}
            />
            <SummaryItem
              title="Left Early:"
              color={attendanceColors["Left Early"]}
              count={attendanceSummary?.left_early}
              hideBorder
            />
            <SummaryItem
              title="On leave:"
              color={attendanceColors["On leave"]}
              count={attendanceSummary?.on_leave}
              hideBorder
            />
          </div>
        </div>
      </div>

      <CustomTable
        loading={isLoading}
        columns={columns}
        dataSource={addKeyInArray(data?.data?.attendance_logs)}
        className="header-table-color"
      />
    </SidebarLayout>
  );
}

interface SummaryItemProps {
  title: string;
  count?: number | string;
  color: string;
  hideBorder?: boolean;
  hideDays?: boolean;
}

const SummaryItem = ({
  title,
  count,
  color,
  hideBorder,
  hideDays,
}: SummaryItemProps) => (
  <div
    className={`flex justify-between items-center border-b py-1.5 ${
      !hideBorder ? "border-extra-light" : "border-transparent"
    }`}
  >
    <h4 className="text-extra-gray text-sm font-medium">{title}</h4>
    <div className="justify-start items-center gap-3 flex">
      <div
        className="w-3 h-3 rounded-full"
        style={{ backgroundColor: color }}
      />
      <h6 className="text-display-body text-sm font-medium min-w-16">
        {count ?? 0}{" "}
        {typeof count === "number" && !hideDays
          ? count === 1 || count === 0
            ? "Day"
            : "Days"
          : ""}
      </h6>
    </div>
  </div>
);

const columnList = [
  "date",
  "shift_name",
  "status",
  "checkin_time",
  "checkin_log_status",
  "check_in_location",
  "check_in_address",
  "checkout_time",
  "checkout_log_status",
  "check_out_location",
  "check_out_address",
  "total_hours",
  "overtime",
  "late_check_in_note",
  "left_early_note",
  "attendance_reconciliation",
];
